import {IDepot, IProductPromoteInput} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {useState} from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import moment, {Moment} from 'moment';
import MomentUtils from '@date-io/moment';

export function PromoteProductForm(props: {
  productId: number;
  depots: IDepot[];
  defaultValues?: IProductPromoteInput;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (product: IProductPromoteInput) => any;
  onCancel?: () => any;
  cancelButtonTitle?: string;
  smallWidth?: boolean;
}) {
  const {isLoading} = props;
  const [isAllDepots, setIsAllDepots] = useState(true);

  type Submission = IProductPromoteInput;
  const {handleSubmit, control} = useForm<Submission>({
    defaultValues: {
      inputProductId: props.productId,
      promotionalStartDate: moment().startOf('day').toDate(),
      promotionalEndDate: moment().endOf('week').toDate(),
      depots: [],
      ...props.defaultValues,
    },
  });

  const onSubmit = (submission: Submission) => {
    props.onSubmit({
      ...submission,
      ...(isAllDepots && {depots: props.depots.map((depot) => depot.id)}),
      promotionalValue: parseFloat(submission.promotionalValue as any),
    });
  };

  const findDepotsByIds = (ids: any[]): IDepot[] => {
    return ids
      .map((id) => props.depots.find((depot) => depot.id === id))
      .filter((depot) => !!depot) as IDepot[];
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div className="generic-form-container">
        <div className="form-container">
          <form className="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} className="form-section">
              <Grid item sm={12}>
                <Typography variant={'subtitle2'} gutterBottom>
                  Price
                </Typography>
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Controller
                  name="promotionalValue"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      className="input"
                      label="Price"
                      variant="outlined"
                      fullWidth
                      type="number"
                      InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={isLoading}
                    />
                  )}
                  rules={{
                    required: 'Price required',
                    validate: (value) => !isNaN(value!),
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className="form-section">
              <Grid item sm={12}>
                <Typography variant={'subtitle2'} gutterBottom>
                  Duration
                </Typography>
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Controller
                  name="promotionalStartDate"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <KeyboardDatePicker
                      className="input"
                      label="Start Date"
                      inputVariant="outlined"
                      fullWidth
                      value={value}
                      onChange={(moment: Moment | null) =>
                        onChange(moment?.startOf('day').toDate())
                      }
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={isLoading}
                      autoOk
                      format="DD/MM/yyyy"
                    />
                  )}
                  rules={{
                    required: 'Start date required',
                  }}
                />
              </Grid>
              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Controller
                  name="promotionalEndDate"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <KeyboardDatePicker
                      className="input"
                      label="End Date"
                      inputVariant="outlined"
                      fullWidth
                      value={value}
                      onChange={(moment: Moment | null) =>
                        onChange(moment?.endOf('day').toDate())
                      }
                      error={!!error}
                      helperText={error ? error.message : null}
                      disabled={isLoading}
                      autoOk
                      format="DD/MM/yyyy"
                    />
                  )}
                  rules={{
                    required: 'End date required',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} className="form-section">
              <Grid item sm={12}>
                <Typography variant={'subtitle2'} gutterBottom>
                  Depots
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <RadioGroup
                  row
                  defaultValue={isAllDepots ? '1' : '0'}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const isAllDepots =
                      (event.target as HTMLInputElement).value === '1';
                    setIsAllDepots(isAllDepots);
                  }}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio color="primary" />}
                    label="All Depots"
                  />
                  <FormControlLabel
                    value="0"
                    control={<Radio color="primary" />}
                    label="Specific Depots"
                  />
                </RadioGroup>
              </Grid>
              {!isAllDepots && (
                <Grid item xs={12}>
                  <Controller
                    name="depots"
                    control={control}
                    render={({
                      field: {onChange, value},
                      fieldState: {error},
                    }) => (
                      <Autocomplete
                        multiple
                        value={findDepotsByIds(value)}
                        options={props.depots}
                        getOptionLabel={(depot: IDepot) => depot.name}
                        renderTags={(value: IDepot[], getTagProps) =>
                          value.map((depot: IDepot, index: number) => (
                            <Chip
                              key={depot.id}
                              variant="outlined"
                              label={depot.name}
                              {...getTagProps({index})}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Depots"
                            error={!!error}
                            helperText={error ? error.message : null}
                            disabled={isLoading}
                          />
                        )}
                        onChange={(event, depots: IDepot[]) => {
                          onChange(depots.map((depot) => depot.id));
                        }}
                      />
                    )}
                    rules={{
                      required: 'Depot required',
                    }}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
}
